import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { BadgeArea, Buit } from '@/pages/operacio/comu';
import { Cerca } from './comu';
import { LlistaPersonesInformar } from './estructura/LlistaPersonesInformar';
import { esActiu, useEstructura } from './marc/estructura';
import './administracio.css';
import './avisos.css';

const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * «A qui s'informa de què?»: tots els serveis agrupats per departament i àrea; en triar-ne un
 * es veu i s'edita la seva llista de persones a informar. Només es carrega la llista del
 * servei seleccionat (no una petició per servei).
 */
export function W23UsuarisInformar() {
  const { t } = useTranslation('administracio');
  const estructura = useEstructura();
  const [params, setParams] = useSearchParams();
  const [cerca, setCerca] = useState('');
  const [inactius, setInactius] = useState(false);
  const seleccionat = params.get('servei') ? Number(params.get('servei')) : null;
  const q = normalitza(cerca.trim());

  const grups = estructura.departaments.flatMap((d) =>
    d.arees
      .map((a) => ({
        clau: a.id,
        titol: `${d.nom} › ${a.nom}`,
        tipus: a.tipus,
        serveis: a.serveis.filter(
          (s) =>
            (inactius || esActiu(s) || s.id === seleccionat) &&
            (!q || normalitza(`${s.nom} ${s.codi ?? ''} ${a.nom} ${d.nom}`).includes(q)),
        ),
      }))
      .filter((g) => g.serveis.length > 0),
  );
  const primer = grups[0]?.serveis[0]?.id ?? null;
  const actiu = seleccionat ?? primer;
  const servei = estructura.servei(actiu);
  const area = estructura.area(servei?.areaId);
  const tria = (id: number) => setParams({ servei: String(id) }, { replace: true });

  return (
    <section className="pantalla" data-screen="W-23">
      <Toolbar titol={t('titols.usuarisInformar')} subtitol={t('avisos.informar.subtitol')} />
      {estructura.error ? (
        <ErrorCarrega onReintenta={estructura.refetch} />
      ) : (
        <div className="lf">
          <div className="lf__llista">
            <div className="lf__eines">
              <Cerca valor={cerca} onCanvia={setCerca} placeholder={t('avisos.informar.cerca')} />
              <label className="switch-row t-footnote">
                {t('marc.mostraInactius')}
                <input
                  type="checkbox"
                  className="switch"
                  checked={inactius}
                  onChange={(e) => setInactius(e.target.checked)}
                />
              </label>
            </div>
            {grups.length === 0 ? (
              <Buit icona="rectangle-stack">
                {estructura.carregant
                  ? t('comu.carregant')
                  : q
                    ? t('avisos.senseResultats')
                    : t('informar.senseLlistes')}
              </Buit>
            ) : (
              <ul className="lf__items" role="listbox" aria-label={t('informar.servei')}>
                {grups.map((g) => [
                  <li key={`g-${g.clau}`} className="lf__grup" role="presentation">
                    {g.titol}
                  </li>,
                  ...g.serveis.map((s) => (
                    <li
                      key={s.id}
                      role="option"
                      tabIndex={0}
                      data-servei={s.id}
                      className={`lf__item${esActiu(s) ? '' : ' is-inactiu'}`}
                      aria-selected={s.id === actiu}
                      onClick={() => tria(s.id)}
                      onKeyDown={(e) => e.key === 'Enter' && tria(s.id)}
                    >
                      <span
                        className={`node-punt node-punt--${g.tipus === 'CRISI' ? 'crisi' : 'guardia_it'}`}
                        aria-hidden="true"
                      />
                      <span className="lf__text">
                        <span className="lf__nom">{s.nom}</span>
                      </span>
                    </li>
                  )),
                ])}
              </ul>
            )}
          </div>
          <div className="lf__fitxa">
            {servei ? (
              <>
                <div className="fitxa-cap">
                  <div className="fitxa-cap__titols">
                    <span className="fitxa-cap__ruta">{estructura.ruta('SERVEI', servei.id)}</span>
                    <h2>{servei.nom}</h2>
                    <div className="fitxa-cap__badges">
                      <BadgeArea tipus={area?.tipus} />
                      {!esActiu(servei) ? (
                        <span className="badge badge--gray">{t('comu.inactiu')}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="fitxa-cap__accions">
                    <Link to={`/admin/arees?servei=${servei.id}`} className="btn btn--s">
                      <Icona nom="building" />
                      {t('avisos.informar.obreServei')}
                    </Link>
                  </div>
                </div>
                <div className="fitxa-cos">
                  <LlistaPersonesInformar key={servei.id} serveiId={servei.id} />
                </div>
              </>
            ) : (
              <Buit icona="rectangle-stack">
                {estructura.carregant ? t('comu.carregant') : t('avisos.informar.tria')}
              </Buit>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
